import {Component, inject, OnDestroy, OnInit, Optional} from '@angular/core';
import {PrimusRouteService} from '../../core/primus-route.service';
import {MediaHelperService} from '../../core/media-helper.service';
import {ObjectEditService} from '../../core/object-edit.service';
import {UiToolsService} from '../../core/ui-tools.service';
import {CurrentObjectService} from '../../core/current-object.service';
import {ContentInfo} from '../../core/definitions/content-info';
import {SectionMenuContainer} from '../../object-view/section-menu/section-menu.component';
import {SuperObjectModel} from '../../core/definitions/super-object-model';
import {OperationAndSectionsContainerParams, OperationService} from '../../operations/operation.service';
import {OperationTarget} from '../../core/definitions/operation-target.enum';
import {OperationStepType} from '../../core/definitions/operation-step-type.enum';
import {ObjectMediaContainer} from '../../core/definitions/object-media-container';
import {OperationContainer} from '../../core/definitions/operation-container';
import {OperationExecutorType} from '../../core/definitions/operation-executor-type.enum';
import {PrimusRouterService} from '../../core/primus-router.service';
import {Subscription} from 'rxjs';
import {SelectorContainer} from '../../core/definitions/selector-container';
import {ActionMenuItem} from '../../object-page-v2/ui/object-page-header/menu-group-button/menu-group-button.component';
import {
  ActionMenuAndOperationDef,
  operationToActionMenu
} from '../../object-page-v2/ui/object-page-header/object-page-operations-toolbar/operationToActionMenu';
import {CurrentSearchContext} from '../../object-search/current-search.context';
import {ActivatedRoute, Router} from '@angular/router';
import {FeatureFlagsService} from '../../core/feature-flags.service';
import {ObjectPageNavigationStack} from '../../object-page-v2/object-page-navigation-stack';
import {RefService} from "../../core/ref.service";
import {SearchSelectorService} from "../../core/search-selector.service";
import {StateParams} from "../../core/definitions/state-params";
import {RefParams} from "../../core/definitions/ref-params";
import {RefData} from "../../core/definitions/ref-data";

@Component({
    selector: 'app-media-page',
    templateUrl: './media-page.component.html',
    styleUrls: ['./media-page.component.scss'],
    standalone: false
})
export class MediaPageComponent implements OnInit, OnDestroy {
  // Injections
  public currentObjectService: CurrentObjectService = inject(CurrentObjectService);
  private readonly featureFlags: FeatureFlagsService = inject(FeatureFlagsService);
  private readonly mediaHelper: MediaHelperService = inject(MediaHelperService);
  private readonly objectEditService: ObjectEditService = inject(ObjectEditService);
  private readonly objectPageNavigationStack: ObjectPageNavigationStack = inject(ObjectPageNavigationStack);
  private readonly operationService: OperationService = inject(OperationService);
  private readonly primusRoute: PrimusRouteService = inject(PrimusRouteService);
  private readonly primusRouter: PrimusRouterService = inject(PrimusRouterService);
  private readonly ref: RefService = inject(RefService);
  private readonly route: ActivatedRoute = inject(ActivatedRoute);
  private readonly router: Router = inject(Router);
  private readonly searchSelectorService: SearchSelectorService = inject(SearchSelectorService);
  private readonly uiTools: UiToolsService = inject(UiToolsService);

  operationContainer: OperationContainer;
  contentInfo = new ContentInfo();
  art: SuperObjectModel = null;
  contextId;
  parentId;
  galleryParentId;
  mediaId;
  imageFullScreen = false;
  sectionMenuContainer = new SectionMenuContainer();
  artTarget;
  mediaContainer: ObjectMediaContainer;
  toggleAnnotations: boolean;
  mediumScreen: boolean;
  smallObjectMenus: boolean;
  smallScreen: boolean;
  scrollContent = false;
  finishedLoading = false;

  headerActions: ActionMenuItem[] = [];

  private mediaObjectType;
  private artifactId;
  private stopSizeWatch;
  private routerSubscription: Subscription;
  // True while the carousel changes the selected media, so that navigation does not reload all media
  private selectingMedia = false;

  constructor(@Optional() public currentSearchContext?: CurrentSearchContext) {}

  ngOnInit() {
    this.featureFlags.initFeatureFlags().then()
    this.routerSubscription = this.primusRouter.navigationHandler(() => {
      if (this.selectingMedia) {
        return;
      }
      this.init().then();
    });
    this.init().then();
  }

  ngOnDestroy(): void {
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
    if (this.stopSizeWatch) {
      this.uiTools.removeWindowSizeListener(this.stopSizeWatch);
    }
  }

  objectRefresh(event) {
    if (event.store) {
      this.storeObject().then();
    } else {
      this.setSelectedMediaItem().then();
    }
  }

  isFinishedLoading() {
    this.finishedLoading = true;
  }
  selectorOverlay() {
    this.searchSelectorService.disableSelector(<SelectorContainer>this.operationContainer);
  }

  async selectMedia(mediaId) {
    this.selectingMedia = true;
    try {
      await this.primusRouter.navigateState(this.primusRouter.currentState(),
        {
          mediaId: mediaId
        });
    } finally {
      this.selectingMedia = false;
    }
    this.mediaId = mediaId;
    this.setSelectedMediaItem().then();
  }

  onOperationClick(operation: ActionMenuAndOperationDef) {
    this.operationService.openOperationView(this.operationContainer, operation).then(res => {
      if (res) {
        console.log('Result: ' + res.status_message);
      }
    });
  }

  onCloseClick() {
    const prevRoute = this.objectPageNavigationStack.pop();
    if (this.currentSearchContext) {
      this.router.navigate(['/search'], {
        queryParams: {
          artifactId: null,
        },
        queryParamsHandling: 'merge'
      })
    } else if (prevRoute) {
      this.router.navigateByUrl(prevRoute);
    } else {
      this.router.navigate(['/'])
    }
  }

  private async init() {
    this.currentObjectService.isEditing = this.primusRoute.params.edit === 'true';
    if (this.primusRoute.params.contextIds && typeof this.primusRoute.params.contextIds === 'string') {
      this.contextId = this.primusRoute.params.contextIds;
      this.parentId = this.primusRoute.params.parentId !== this.contextId ? this.primusRoute.params.parentId : undefined;
    } else if (this.primusRoute.params.parentId) {
      this.contextId = this.primusRoute.params.parentId;
      this.parentId = null;
    }
    this.galleryParentId = this.primusRoute.params.galleryParentId;
    this.mediaId = this.primusRoute.params.mediaId;
    this.mediaObjectType = this.primusRoute.params.objectType;


    if (this.parentId) {
      this.artifactId = this.parentId;
    } else if (this.contextId) {
      this.artifactId = this.contextId;
    } else if (this.mediaId) {
      this.artifactId = this.mediaId;
    } else {
      console.warn('Could not get artifact id for retrieving media');
    }

    await this.loadMedia();

    const windowSize = this.uiTools.windowSize;
    this.mediumScreen = windowSize.width < 1025;
    this.smallObjectMenus = windowSize.width < 870;
    this.smallScreen = windowSize.width < 642;

    this.stopSizeWatch = this.uiTools.addWindowSizeListener(newVal => {
        this.smallObjectMenus = newVal.width < 1025;
        this.mediumScreen = newVal.width < 1025;
        this.smallScreen = newVal.width < 642;
      }
    );
  }

  private async loadMedia() {
    let object: SuperObjectModel;
    try {
      object = await this.objectEditService.loadObject(this.artifactId);
    } catch (reason) {
      console.warn(`Unable to load object: ${reason.error.message}`);
      return;
    }
    if (this.mediaHelper.objectIsMedia(object)) {
      // This happens if you click on a media based search item
      this.mediaContainer = await this.mediaHelper.getMediaContainerForMediaObject(object);
    } else {
      // This happens if you open an artifact containing images or videos
      this.artTarget = object;
      this.mediaContainer = await this.mediaHelper.getMediaContainerForObjectAndObjectType(
        this.artTarget, this.mediaObjectType);
    }
    await this.setSelectedMediaItem();
  }

  private getCreateParams(): OperationAndSectionsContainerParams {
    const params = {
      useExistingObject: true,
      objectId: this.mediaId,
      getSourceObject: !this.currentObjectService.isEditing,
      operationTarget: OperationTarget.OBJECT_VIEW,
      templateGroupId: this.primusRoute.params.template_group_id
    } as OperationAndSectionsContainerParams;
    if (this.currentObjectService.isEditing) {
      params.executorType = OperationExecutorType.ARTIFACT;
      params.operationStepType = OperationStepType.EDIT_OBJECT;
    }
    return params;
  }

  private async setSelectedMediaItem() {
    this.operationContainer = await this.operationService.createOperationAndRootSectionsContainer(this.getCreateParams());
    this.postLoadOperations();
  }

  private postLoadOperations() {
    this.sectionMenuContainer.sections = this.operationContainer.rootSectionsContainer.sections;
    this.art = this.operationContainer.rootSectionsContainer.rootObject;

    this.art['$$context_id'] = this.contextId;
    this.art['$$parent_id'] = this.parentId;
    this.contentInfo.updateContentInfo(
      this.currentObjectService.isEditing, this.art, this.primusRoute.params.listName);
    this.sectionMenuContainer.artifact = this.art;
    this.sectionMenuContainer.contentInfo = this.contentInfo;
    this.currentObjectService.currentObject = this.art;
    this.operationContainer.contentInfo = this.contentInfo;
    this.headerActions = this.operationContainer.operations.map(operationToActionMenu)
  }

  private async storeObject() {
    const value = await this.objectEditService.setObjectValuesStoreObject(this.operationContainer.rootSectionsContainer);
    this.art.artifact_id = value.artifact_id;
    this.currentObjectService.isEditing = false;
    await this.primusRouter.navigateState(this.primusRouter.currentState(), {edit: null, isNew: null}, {
      location: 'replace',
      reload: true,
    });
    this.objectRefresh({store: false});
  }

  openImageFullScreen(activeImage) {
    this.toggleAnnotations = activeImage.toggleAnnotations;
    this.imageFullScreen = true;
  }

  closeImageFullScreen() {
    this.imageFullScreen = false;
  }

  async onBackClick() {
    const currentIdx = this.currentSearchContext?.findResultIndex(this.artifactId);
    if (currentIdx > 0) {
      const prevArtifact = this.currentSearchContext?.searchResults?.[currentIdx - 1];
      const params = new RefParams(prevArtifact, {
        listName: null
      } as StateParams);
      params.searchItemIndex = currentIdx - 1;
      params.rootObjId = prevArtifact.artifact_id;
      const refData = await this.ref.makeRef(params);
      if (refData.stateName.endsWith('artifact')) {
        this.goToArtifact(prevArtifact.artifact_id)
      } else {
        this.goToNotArtifact(refData)
      }
    }
  }

  async onForwardClick() {
    const currentIdx = this.currentSearchContext?.findResultIndex(this.artifactId);
    if (currentIdx < this.currentSearchContext?.resultSize - 1) {
      const nextArtifact = this.currentSearchContext?.searchResults?.[currentIdx + 1];
      const params = new RefParams(nextArtifact, {
        listName: null
      } as StateParams);
      params.searchItemIndex = currentIdx + 1;
      params.rootObjId = nextArtifact.artifact_id;
      const refData = await this.ref.makeRef(params);
      if (refData.stateName.endsWith('artifact')) {
        this.goToArtifact(nextArtifact.artifact_id)
      } else {
        this.goToNotArtifact(refData)
      }
    }
  }

  private goToArtifact(artifactId: string) {
    if (!location.pathname.startsWith('/search/artifact')) {
      this.router.navigate(['/artifact', artifactId], {
        queryParams: {
          artifactId
        },
        queryParamsHandling: 'merge'
      }).then();
    }
    else {
      this.router.navigate(['..', artifactId], {
        relativeTo: this.route,
        queryParams: {
          artifactId
        },
        queryParamsHandling: 'merge'
      })
    }
  }

  private goToNotArtifact(refData: RefData) {
    this.router.navigate(refData.routerLink, {
      queryParams: refData.queryParams,
      queryParamsHandling: 'merge'
    })
  }

  readonly newLayout = this.featureFlags.getFeatureFlags()?.experimental.useNewMainMenu;
  readonly objectPageV2 = this.featureFlags.getFeatureFlags()?.experimental.useNewObjectPage;
}
